import { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import api from '../utils/api';

const API_BASE = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace('/api', '')
  : '';

export function useChat(user) {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [typingUsers, setTypingUsers] = useState({});
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [userStatuses, setUserStatuses] = useState({}); // userId -> { status, statusText }
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const typingTimeoutRef = useRef({});
  const socketRef = useRef(null);
  const activeConvRef = useRef(null);
  const loadConversationsRef = useRef(null);

  // Keep ref in sync
  activeConvRef.current = activeConversationId;

  // ── Connect socket ──
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    const s = io(`${API_BASE}/chat`, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
    });

    s.on('connect', () => setConnected(true));
    s.on('disconnect', () => setConnected(false));

    s.on('message:new', (msg) => {
      // Update messages if viewing this conversation
      if (activeConvRef.current === msg.conversation_id) {
        setMessages(prev => {
          if (prev.some(m => m.id === msg.id)) return prev;
          return [...prev, msg];
        });
        // Auto-mark as read if we're viewing this conversation and it's not our own message
        if (msg.sender_id !== user.id) {
          s.emit('messages:read', { conversationId: msg.conversation_id });
        }
      }

      // Update conversation list (last message, unread)
      setConversations(prev => {
        const idx = prev.findIndex(c => c.id === msg.conversation_id);
        if (idx === -1) return prev;
        const updated = [...prev];
        const conv = { ...updated[idx] };
        conv.last_message = {
          id: msg.id,
          content: msg.content,
          type: msg.type,
          sender_id: msg.sender_id,
          sender_name: msg.sender_first_name,
          created_at: msg.created_at,
          is_deleted: msg.is_deleted,
        };
        conv.updated_at = msg.created_at;
        if (activeConvRef.current !== msg.conversation_id && msg.sender_id !== user.id) {
          conv.unread_count = (parseInt(conv.unread_count) || 0) + 1;
        }
        updated[idx] = conv;
        updated.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
        return updated;
      });
    });

    s.on('conversation:updated', ({ conversationId, lastMessage }) => {
      setConversations(prev => {
        const idx = prev.findIndex(c => c.id === conversationId);
        if (idx === -1) {
          loadConversations();
          return prev;
        }
        const updated = [...prev];
        const conv = { ...updated[idx] };
        conv.last_message = lastMessage ? {
          id: lastMessage.id,
          content: lastMessage.content,
          type: lastMessage.type,
          sender_id: lastMessage.sender_id,
          sender_name: lastMessage.sender_first_name,
          created_at: lastMessage.created_at,
          is_deleted: lastMessage.is_deleted,
        } : conv.last_message;
        conv.updated_at = new Date().toISOString();
        if (activeConvRef.current !== conversationId) {
          conv.unread_count = (parseInt(conv.unread_count) || 0) + 1;
        }
        updated[idx] = conv;
        updated.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
        return updated;
      });
    });

    s.on('message:edited', ({ messageId, content, editedAt }) => {
      setMessages(prev => prev.map(m =>
        m.id === messageId ? { ...m, content, is_edited: true, edited_at: editedAt } : m
      ));
    });

    s.on('message:deleted', ({ messageId }) => {
      setMessages(prev => prev.map(m =>
        m.id === messageId ? { ...m, is_deleted: true, content: null } : m
      ));
    });

    s.on('typing:update', ({ conversationId, userId: typerId, name, isTyping }) => {
      setTypingUsers(prev => {
        const convTyping = { ...(prev[conversationId] || {}) };
        if (isTyping) {
          convTyping[typerId] = name;
        } else {
          delete convTyping[typerId];
        }
        return { ...prev, [conversationId]: convTyping };
      });
    });

    s.on('messages:read', ({ conversationId, userId: readerId, readAt, firstName, lastName, avatarUrl }) => {
      if (activeConvRef.current !== conversationId) return;
      setMessages(prev => prev.map(m => {
        // Add receipts to own messages + system messages (for admins)
        const isSystem = m.type === 'system' || m.sender_id === null;
        if (m.sender_id !== user.id && !(isSystem && user.role === 'admin')) return m;
        const readBy = [...(m.read_by || [])];
        if (readBy.find(r => r.user_id === readerId)) return m;
        readBy.push({ user_id: readerId, read_at: readAt, first_name: firstName, last_name: lastName, avatar_url: avatarUrl, message_id: m.id });
        return { ...m, read_by: readBy };
      }));
    });

    s.on('reaction:added', ({ messageId, userId: reacterId, emoji, userName }) => {
      setMessages(prev => prev.map(m => {
        if (m.id !== messageId) return m;
        const reactions = [...(m.reactions || [])];
        if (!reactions.find(r => r.userId === reacterId && r.emoji === emoji)) {
          reactions.push({ emoji, userId: reacterId, userName });
        }
        return { ...m, reactions };
      }));
    });

    s.on('reaction:removed', ({ messageId, userId: reacterId, emoji }) => {
      setMessages(prev => prev.map(m => {
        if (m.id !== messageId) return m;
        const reactions = (m.reactions || []).filter(
          r => !(r.userId === reacterId && r.emoji === emoji)
        );
        return { ...m, reactions };
      }));
    });

    s.on('user:online', ({ userId: uid, chatStatus, chatStatusText }) => {
      setOnlineUsers(prev => new Set([...prev, uid]));
      if (chatStatus) {
        setUserStatuses(prev => ({ ...prev, [uid]: { status: chatStatus, statusText: chatStatusText || '' } }));
      }
    });

    s.on('user:offline', ({ userId: uid }) => {
      setOnlineUsers(prev => {
        const next = new Set(prev);
        next.delete(uid);
        return next;
      });
    });

    // Initial online roster — learn who's already online when we connect
    s.on('users:roster', (roster) => {
      const ids = new Set(roster.map(r => r.userId));
      setOnlineUsers(ids);
      const statuses = {};
      roster.forEach(r => { statuses[r.userId] = { status: r.chatStatus, statusText: r.chatStatusText || '' }; });
      setUserStatuses(prev => ({ ...prev, ...statuses }));
    });

    // New conversation created by someone else — join socket room + reload list
    s.on('conversation:created', ({ conversationId }) => {
      s.emit('conversation:join', { conversationId });
      loadConversationsRef.current?.();
    });

    // ── Phase 3: Chat status updates ──
    s.on('user:status', ({ userId: uid, status, statusText }) => {
      setUserStatuses(prev => ({ ...prev, [uid]: { status, statusText: statusText || '' } }));
    });

    // ── Phase 3: @mention notifications ──
    // Handled by ChatContext (with custom toast UI). Keeping a second handler
    // here would fire two toasts per mention.

    socketRef.current = s;
    setSocket(s);

    return () => {
      s.disconnect();
      socketRef.current = null;
    };
  }, [user?.id]); // eslint-disable-line

  // ── Load conversations ──
  const loadConversations = useCallback(async () => {
    try {
      setLoadingConversations(true);
      const res = await api.get('/chat/conversations');
      setConversations(res.data);
    } catch (err) {
      console.error('Failed to load conversations:', err);
    } finally {
      setLoadingConversations(false);
    }
  }, []);
  loadConversationsRef.current = loadConversations;

  useEffect(() => {
    if (user) loadConversations();
  }, [user, loadConversations]);

  // ── Load messages for active conversation ──
  const loadMessages = useCallback(async (conversationId, before) => {
    try {
      setLoadingMessages(true);
      const params = { limit: 50 };
      if (before) params.before = before;
      const res = await api.get(`/chat/conversations/${conversationId}/messages`, { params });
      if (before) {
        setMessages(prev => [...res.data, ...prev]);
      } else {
        setMessages(res.data);
      }
      setHasMoreMessages(res.data.length === 50);
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  // Switch active conversation
  const selectConversation = useCallback((convId) => {
    setActiveConversationId(convId);
    setMessages([]);
    setHasMoreMessages(true);
    if (convId) {
      // Join the socket room every time, not just on connect. The connect-time
      // auto-join only covers conversations that existed when the socket came up,
      // so a DM started mid-session left the creator outside their own room: their
      // messages reached the other side and never echoed back until a reload.
      // socket.join() is idempotent, so re-joining an already-joined room is free.
      socketRef.current?.emit('conversation:join', { conversationId: convId });
      loadMessages(convId);
      setConversations(prev => prev.map(c =>
        c.id === convId ? { ...c, unread_count: 0 } : c
      ));
      socketRef.current?.emit('messages:read', { conversationId: convId });
    }
  }, [loadMessages]);

  // Load more (older messages)
  const loadMore = useCallback(() => {
    if (!activeConversationId || !messages.length || loadingMessages) return;
    const oldest = messages[0];
    if (oldest) loadMessages(activeConversationId, oldest.id);
  }, [activeConversationId, messages, loadingMessages, loadMessages]);

  // ── Actions ──
  const sendMessage = useCallback((conversationId, content, type = 'text', replyToId, attachments) => {
    return new Promise((resolve, reject) => {
      socketRef.current?.emit('message:send', {
        conversationId, content, type, replyToId, attachments,
      }, (response) => {
        if (response?.error) reject(new Error(response.error));
        else resolve(response?.message);
      });
    });
  }, []);

  const editMessage = useCallback((messageId, content) => {
    return new Promise((resolve, reject) => {
      socketRef.current?.emit('message:edit', { messageId, content }, (res) => {
        if (res?.error) reject(new Error(res.error));
        else resolve();
      });
    });
  }, []);

  const deleteMessage = useCallback((messageId) => {
    return new Promise((resolve, reject) => {
      socketRef.current?.emit('message:delete', { messageId }, (res) => {
        if (res?.error) reject(new Error(res.error));
        else resolve();
      });
    });
  }, []);

  const startTyping = useCallback((conversationId) => {
    socketRef.current?.emit('typing:start', { conversationId });
    if (typingTimeoutRef.current[conversationId]) {
      clearTimeout(typingTimeoutRef.current[conversationId]);
    }
    typingTimeoutRef.current[conversationId] = setTimeout(() => {
      socketRef.current?.emit('typing:stop', { conversationId });
    }, 3000);
  }, []);

  const stopTyping = useCallback((conversationId) => {
    socketRef.current?.emit('typing:stop', { conversationId });
    if (typingTimeoutRef.current[conversationId]) {
      clearTimeout(typingTimeoutRef.current[conversationId]);
    }
  }, []);

  const markAsRead = useCallback((conversationId) => {
    socketRef.current?.emit('messages:read', { conversationId });
    setConversations(prev => prev.map(c =>
      c.id === conversationId ? { ...c, unread_count: 0 } : c
    ));
  }, []);

  const toggleReaction = useCallback((messageId, emoji) => {
    return new Promise((resolve, reject) => {
      socketRef.current?.emit('reaction:toggle', { messageId, emoji }, (res) => {
        if (res?.error) reject(new Error(res.error));
        else resolve(res?.action);
      });
    });
  }, []);

  const createConversation = useCallback(async (type, name, description, memberIds) => {
    const res = await api.post('/chat/conversations', { type, name, description, memberIds });
    // The server emits 'conversation:created' to the OTHER members, never to the
    // creator — so the creator has to join their own new room here.
    if (res.data?.id) socketRef.current?.emit('conversation:join', { conversationId: res.data.id });
    await loadConversations();
    return res.data;
  }, [loadConversations]);

  const uploadFile = useCallback(async (file) => {
    const form = new FormData();
    form.append('file', file);
    const res = await api.post('/chat/upload', form);
    return res.data;
  }, []);

  const searchMessages = useCallback(async (query) => {
    const res = await api.get('/chat/messages/search', { params: { q: query } });
    return res.data;
  }, []);

  // ── Phase 3: Set chat status ──
  const setChatStatus = useCallback((status, statusText) => {
    return new Promise((resolve, reject) => {
      socketRef.current?.emit('status:set', { status, statusText }, (res) => {
        if (res?.error) reject(new Error(res.error));
        else resolve();
      });
    });
  }, []);

  // ── Leave / archive conversation ──
  const leaveConversation = useCallback(async (conversationId) => {
    await api.delete(`/chat/conversations/${conversationId}/members/${user.id}`);
    setConversations(prev => prev.filter(c => c.id !== conversationId));
    if (activeConvRef.current === conversationId) {
      setActiveConversationId(null);
      setMessages([]);
    }
  }, [user?.id]);

  const deleteConversation = useCallback(async (conversationId) => {
    await api.delete(`/chat/conversations/${conversationId}`);
    setConversations(prev => prev.filter(c => c.id !== conversationId));
    if (activeConvRef.current === conversationId) {
      setActiveConversationId(null);
      setMessages([]);
    }
  }, []);

  // ── Remove member from conversation ──
  const removeMember = useCallback(async (conversationId, userId) => {
    await api.delete(`/chat/conversations/${conversationId}/members/${userId}`);
  }, []);

  // ── Add members to conversation ──
  const addMembers = useCallback(async (conversationId, userIds) => {
    await api.post(`/chat/conversations/${conversationId}/members`, { userIds });
  }, []);

  // ── Phase 3: Create/get ticket thread ──
  const createTicketThread = useCallback(async (ticketId) => {
    const res = await api.post(`/chat/ticket-thread/${ticketId}`);
    if (!res.data.existing) await loadConversations();
    return res.data;
  }, [loadConversations]);

  // Total unread count
  const totalUnread = conversations.reduce((sum, c) => sum + (parseInt(c.unread_count) || 0), 0);

  return {
    socket, connected, socketRef,
    conversations, loadingConversations,
    activeConversationId, selectConversation,
    messages, loadingMessages, hasMoreMessages, loadMore,
    typingUsers, onlineUsers, userStatuses,
    sendMessage, editMessage, deleteMessage,
    startTyping, stopTyping, markAsRead, toggleReaction,
    createConversation, uploadFile, searchMessages,
    setChatStatus, createTicketThread,
    leaveConversation, deleteConversation, removeMember, addMembers,
    totalUnread, loadConversations,
  };
}
